import { useState } from 'react';
import { Box, Button, Flex, HStack, Stack, Text } from '@chakra-ui/react';
import { BarChart3, Check } from 'lucide-react';
import { myVotes, showsResults, voterCount } from '../../lib/polls';
import { appLocale } from '../../lib/format';
import type { ChatMessage } from './messageUtils';
import { useT } from '../../i18n';

// A poll in the chat (M60, D220): options to tap until you've voted; then
// (or once it's closed) bars with counts and who chose what, by name, since
// votes are metadata everyone in the chat can see.

export interface PollActions {
  vote: (message: ChatMessage, choices: number[] | null) => void;
  close: (message: ChatMessage) => void;
  /** What you call this person (D143), or their name. */
  nameOf: (userId: string) => string;
}

function closeLabel(iso: string): string {
  const date = new Date(iso);
  const sameDay = date.toDateString() === new Date().toDateString();
  return date.toLocaleString(appLocale(), {
    ...(sameDay ? {} : { weekday: 'short' }),
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function PollCard({
  message,
  currentUserId,
  isOwn,
  actions,
}: {
  message: ChatMessage;
  currentUserId: string | undefined;
  isOwn: boolean;
  actions: PollActions;
}) {
  const t = useT();
  const content = message.e2ee?.envelope?.poll;
  const poll = message.poll;
  const [changing, setChanging] = useState(false);
  const mine = poll ? myVotes(poll, currentUserId) : [];
  const [picked, setPicked] = useState<number[]>([]);
  if (!content || !poll) {
    return (
      <Text fontStyle="italic" color="muted" fontSize="15px">
        {t('chat.message.updateApp')}
      </Text>
    );
  }

  const results = showsResults(poll, currentUserId) && !changing;
  const voters = voterCount(poll);
  const most = Math.max(1, ...poll.counts);

  const choose = (index: number) => {
    if (poll.multiple) {
      setPicked((list) =>
        list.includes(index)
          ? list.filter((value) => value !== index)
          : [...list, index]
      );
      return;
    }
    setChanging(false);
    actions.vote(message, [index]);
  };

  return (
    // Never wider than its bubble on a narrow phone (M68, D259).
    <Box
      minW={{ base: 'min(240px, 100%)', md: 'min(280px, 100%)' }}
      maxW="320px"
      data-testid="poll"
    >
      <HStack spacing={2} align="flex-start">
        <Box color="accent" pt="3px" flexShrink={0}>
          <BarChart3 size={18} aria-hidden="true" />
        </Box>
        <Text fontWeight={700} overflowWrap="anywhere">
          {content.question}
        </Text>
      </HStack>
      <Text fontSize="13px" color="muted" pb={2}>
        {poll.closed
          ? t('chat.poll.closed')
          : [
              t(poll.multiple ? 'chat.poll.chooseAny' : 'chat.poll.chooseOne'),
              poll.closesAt
                ? t('chat.poll.closesAt', { time: closeLabel(poll.closesAt) })
                : null,
            ]
              .filter(Boolean)
              .join(' · ')}
      </Text>

      {results ? (
        <Stack spacing={2} role="list">
          {content.options.map((option, index) => {
            const count = poll.counts[index] ?? 0;
            const names = poll.votes
              .filter((vote) => vote.optionIndex === index)
              .map((vote) =>
                vote.userId === currentUserId
                  ? t('common.you')
                  : actions.nameOf(vote.userId)
              );
            const chosen = mine.includes(index);
            return (
              <Box key={index} role="listitem">
                <HStack justify="space-between" spacing={2} fontSize="15px">
                  <HStack spacing={1} minW={0}>
                    {chosen && (
                      <Check
                        size={14}
                        aria-hidden="true"
                        color="currentColor"
                      />
                    )}
                    <Text
                      fontWeight={chosen ? 700 : 500}
                      overflowWrap="anywhere"
                    >
                      {option}
                    </Text>
                  </HStack>
                  <Text
                    fontWeight={700}
                    flexShrink={0}
                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {count}
                  </Text>
                </HStack>
                <Box h="6px" borderRadius="full" bg="surface.subtle" mt="4px">
                  <Box
                    h="full"
                    borderRadius="full"
                    bg={chosen ? 'accent' : 'gray.400'}
                    w={`${Math.round((count / most) * 100)}%`}
                    transition="width 200ms ease"
                  />
                </Box>
                {names.length > 0 && (
                  <Text fontSize="12px" color="muted" pt="2px" noOfLines={2}>
                    {t('chat.poll.votedBy', { names: names.join(', ') })}
                  </Text>
                )}
              </Box>
            );
          })}
        </Stack>
      ) : (
        <Stack spacing={2}>
          {content.options.map((option, index) => {
            const on = poll.multiple ? picked.includes(index) : false;
            return (
              <Button
                key={index}
                justifyContent="flex-start"
                whiteSpace="normal"
                textAlign="left"
                h="auto"
                minH="44px"
                py={2}
                variant="outline"
                colorScheme={on ? 'brand' : 'gray'}
                borderColor={on ? 'accent' : 'line'}
                bg={on ? 'accent.tint' : 'surface'}
                fontWeight={600}
                role={poll.multiple ? 'checkbox' : undefined}
                aria-checked={poll.multiple ? on : undefined}
                onClick={() => choose(index)}
              >
                {option}
              </Button>
            );
          })}
          {poll.multiple && (
            <Button
              colorScheme="brand"
              isDisabled={picked.length === 0}
              onClick={() => {
                setChanging(false);
                actions.vote(
                  message,
                  [...picked].sort((a, b) => a - b)
                );
              }}
            >
              {t('chat.poll.vote')}
            </Button>
          )}
        </Stack>
      )}

      <Flex pt={2} gap={2} align="center" flexWrap="wrap" fontSize="13px">
        <Text color="muted" flex="1">
          {voters === 1
            ? t('chat.poll.votesOne')
            : t('chat.poll.votesOther', { n: voters })}
        </Text>
        {results && !poll.closed && mine.length > 0 && (
          <>
            <Button
              size="xs"
              variant="ghost"
              onClick={() => {
                setPicked(mine);
                setChanging(true);
              }}
            >
              {t('chat.poll.changeVote')}
            </Button>
            <Button
              size="xs"
              variant="ghost"
              colorScheme="gray"
              onClick={() => actions.vote(message, null)}
            >
              {t('chat.poll.retract')}
            </Button>
          </>
        )}
        {isOwn && !poll.closed && (
          <Button
            size="xs"
            variant="outline"
            colorScheme="gray"
            borderColor="line"
            onClick={() => actions.close(message)}
          >
            {t('chat.poll.close')}
          </Button>
        )}
      </Flex>
    </Box>
  );
}
